/**
 * Lightweight audio service. Plays admin-uploaded files when available,
 * falls back to Web-Audio-synthesized cues so the game has sound on a
 * fresh DB. Honours the user's SFX/Music volume sliders in real time.
 *
 *   import { audio } from '@/audio/AudioService';
 *   audio.play('match.tap_correct');
 *   audio.startBgm('bgm.app');
 *   audio.stopBgm('bgm.app');
 */
import { useSettingsStore } from '@/stores/settingsStore';
import { SERVER_URL } from '@/config/serverUrl';
import { AUDIO_KEYS_BY_KEY, type AudioKeySpec } from '@shared/audioKeys';

interface ManifestItem {
  key: string;
  url: string | null;
  volume: number;
  enabled: boolean;
  loop?: boolean;
  category: string;
}

const BASE = SERVER_URL;

/** Resolve `/uploads/audio/x.mp3` → fully qualified URL. On web with empty
 * BASE this stays relative (nginx proxies); on native it gets prefixed with
 * the production API host so capacitor:// pages can still fetch the file. */
function absoluteUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (!BASE) return url;
  return `${BASE}${url.startsWith('/') ? '' : '/'}${url}`;
}

class AudioServiceImpl {
  private manifest = new Map<string, ManifestItem>();
  private loaded = false;
  // Lazy-built audio nodes keyed by slot.
  private bufferPool = new Map<string, HTMLAudioElement[]>();
  private bgmEls = new Map<string, HTMLAudioElement>();
  private ctx: AudioContext | null = null;
  /** Some browsers block audio until the first user gesture. We register a
   * one-shot listener that "unlocks" by creating + resuming the AudioContext
   * inside that gesture. */
  private unlocked = false;

  async loadManifest(): Promise<void> {
    if (this.loaded) return;
    try {
      const res = await fetch(`${BASE}/audio/manifest`, {
        credentials: 'omit',
        headers: { 'ngrok-skip-browser-warning': 'true' },
      });
      if (res.ok) {
        const data = await res.json();
        for (const item of data.items as ManifestItem[]) {
          this.manifest.set(item.key, item);
        }
      }
    } catch (e) {
      console.warn('[audio] manifest fetch failed', e);
    }
    this.loaded = true;
  }

  installUnlockHandler(): void {
    if (this.unlocked) return;
    const unlock = () => {
      this.unlocked = true;
      try {
        const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (Ctx && !this.ctx) this.ctx = new Ctx();
        void this.ctx?.resume();
      } catch { /* */ }
      // Resume any BGM that was queued before unlock.
      for (const el of this.bgmEls.values()) {
        if (el.paused) el.play().catch(() => { /* still gated */ });
      }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  /** Resolved volume = manifest * user setting (SFX or Music). */
  private effectiveVolume(item: ManifestItem | undefined, spec: AudioKeySpec): number {
    const s = useSettingsStore.getState();
    const userSlider = spec.category === 'bgm' ? s.music / 100 : s.sfx / 100;
    const slot = item?.volume ?? spec.defaultVolume;
    return Math.max(0, Math.min(1, slot * userSlider));
  }

  /** Fire-and-forget SFX. Cancels nothing — calling again overlaps. */
  play(key: string): void {
    const spec = AUDIO_KEYS_BY_KEY[key];
    if (!spec) return;
    const item = this.manifest.get(key);
    if (item && item.enabled === false) return;
    const vol = this.effectiveVolume(item, spec);
    if (vol <= 0) return;

    if (item?.url) {
      this.playFile(key, item.url, vol);
    } else {
      this.playSynth(key, vol);
    }
  }

  /** Start a looped BGM. Idempotent (re-calls re-attach volume only). */
  startBgm(key: string): void {
    const spec = AUDIO_KEYS_BY_KEY[key];
    if (!spec || !spec.loop) return;
    const item = this.manifest.get(key);
    if (!item || !item.url) return; // BGM has no synth fallback (too long)
    if (item.enabled === false) return;
    let el = this.bgmEls.get(key);
    if (!el) {
      el = new Audio(absoluteUrl(item.url));
      el.loop = true;
      el.preload = 'auto';
      this.bgmEls.set(key, el);
    }
    el.volume = this.effectiveVolume(item, spec);
    if (el.paused) {
      el.play().catch(() => { /* gated by user-gesture; will auto-resume on unlock */ });
    }
  }

  stopBgm(key: string): void {
    const el = this.bgmEls.get(key);
    if (!el) return;
    el.pause();
    el.currentTime = 0;
  }

  /** Called after manifest loads (or on screen change) to (re)start the
   * correct BGM track. Mirrors the App.tsx screen→BGM mapping so the service
   * can self-apply after a deferred manifest fetch. */
  applyScreenBgm(screen: string): void {
    const stop = (keep: string | null) => {
      for (const k of ['bgm.app', 'bgm.lobby', 'bgm.matchmaking', 'bgm.match'] as const) {
        if (k !== keep) this.stopBgm(k);
      }
    };
    if (screen === 'login') { stop(null); return; }
    if (screen === 'match') { stop('bgm.match'); this.startBgm('bgm.match'); return; }
    if (screen === 'matching') { stop('bgm.matchmaking'); this.startBgm('bgm.matchmaking'); return; }
    if (screen === 'room') { stop('bgm.lobby'); this.startBgm('bgm.lobby'); return; }
    stop('bgm.app');
    this.startBgm('bgm.app');
  }

  /** Pause every BGM in place (preserves playback position) — used when the
   * native app goes to background so we don't bleed audio into other apps. */
  pauseAllBgm(): void {
    for (const el of this.bgmEls.values()) {
      if (!el.paused) el.pause();
    }
  }

  /** Resume the BGM tracks that were paused. Skips any that were stopped
   * (currentTime === 0 means stopBgm was called between pause and resume). */
  resumeAllBgm(): void {
    for (const el of this.bgmEls.values()) {
      if (el.paused && el.currentTime > 0) el.play().catch(() => { /* gate */ });
    }
  }

  /** Call when SFX/Music sliders change so live BGM volume updates. */
  refreshVolumes(): void {
    for (const [key, el] of this.bgmEls) {
      const spec = AUDIO_KEYS_BY_KEY[key];
      const item = this.manifest.get(key);
      if (spec) el.volume = this.effectiveVolume(item, spec);
    }
  }

  // ── Internals ──────────────────────────────────────────────────────
  private playFile(key: string, url: string, volume: number): void {
    let pool = this.bufferPool.get(key);
    if (!pool) {
      pool = [];
      this.bufferPool.set(key, pool);
    }
    // Reuse a finished element if there is one; otherwise allocate a new
    // (cap pool to 4 concurrent to avoid memory blowup on spammed taps).
    let el = pool.find((a) => a.paused || a.ended);
    if (!el) {
      if (pool.length >= 4) {
        el = pool[0]!;
        el.pause();
        el.currentTime = 0;
      } else {
        el = new Audio(absoluteUrl(url));
        el.preload = 'auto';
        pool.push(el);
      }
    }
    el.volume = volume;
    el.currentTime = 0;
    el.play().catch(() => { /* user-gesture gate; ignore until unlock */ });
  }

  /** Synthesized fallback per key — short Web-Audio blips so the game has
   * audio even when no MP3 has been uploaded. Each branch is tiny on purpose. */
  private playSynth(key: string, volume: number): void {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      if (!this.ctx) this.ctx = new Ctx();
      const ctx = this.ctx!;
      if (ctx.state === 'suspended') void ctx.resume();
      const t0 = ctx.currentTime;
      const burst = (freq: number, dur: number, type: OscillatorType = 'sine', gainScale = 1) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        g.gain.setValueAtTime(volume * gainScale, t0);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
        osc.connect(g).connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + dur);
      };

      switch (key) {
        case 'ui.tap':
          burst(880, 0.06, 'square', 0.4);
          break;
        case 'ui.tap_primary':
          burst(660, 0.09, 'triangle', 0.7);
          break;
        case 'ui.toggle':
          burst(550, 0.05, 'square', 0.4);
          break;
        case 'ui.error':
          burst(220, 0.18, 'sawtooth', 0.7);
          break;
        case 'ui.notify_friend_request':
          burst(880, 0.15);
          window.setTimeout(() => burst(1320, 0.18), 110);
          break;
        case 'ui.notify_room_invite':
          burst(1040, 0.12, 'triangle');
          window.setTimeout(() => burst(1560, 0.15, 'triangle'), 80);
          window.setTimeout(() => burst(2080, 0.2, 'triangle'), 180);
          break;
        case 'ui.notify_dm':
          burst(1320, 0.12, 'triangle');
          break;
        case 'ui.message_send':
          burst(1760, 0.04, 'triangle', 0.5);
          break;
        case 'ui.coin_pickup':
          burst(1320, 0.06, 'square');
          window.setTimeout(() => burst(1760, 0.08, 'square'), 60);
          break;
        case 'ui.level_up':
        case 'ui.rank_up': {
          const notes = [523, 659, 784, 1047];
          notes.forEach((f, i) => window.setTimeout(() => burst(f, 0.18, 'triangle', 0.7), i * 90));
          break;
        }
        case 'ui.shop_purchase':
          burst(880, 0.08, 'square');
          window.setTimeout(() => burst(1320, 0.12, 'square'), 70);
          break;

        case 'match.found':
          burst(440, 0.18, 'triangle');
          window.setTimeout(() => burst(880, 0.25, 'triangle'), 180);
          break;
        case 'match.countdown':
          burst(880, 0.1, 'square');
          break;
        case 'match.go':
          burst(440, 0.25, 'sawtooth');
          window.setTimeout(() => burst(880, 0.3, 'sawtooth'), 60);
          break;
        case 'match.tap_correct':
          burst(1320, 0.04, 'triangle', 0.6);
          break;
        case 'match.tap_wrong':
          burst(180, 0.14, 'sawtooth', 0.8);
          break;
        case 'match.combo_3':
          burst(1100, 0.06, 'square', 0.6);
          window.setTimeout(() => burst(1320, 0.08, 'square', 0.6), 50);
          break;
        case 'match.combo_milestone':
          [880, 1100, 1320, 1760].forEach((f, i) => window.setTimeout(() => burst(f, 0.1, 'triangle'), i * 50));
          break;
        case 'match.skill_disrupt':
          burst(420, 0.4, 'sawtooth', 0.7);
          break;
        case 'match.skill_shield':
          burst(660, 0.3, 'sine');
          window.setTimeout(() => burst(880, 0.25, 'sine'), 80);
          break;
        case 'match.skill_absorbed':
          burst(1320, 0.08, 'sine');
          window.setTimeout(() => burst(880, 0.12, 'sine'), 60);
          break;
        case 'match.fall':
          burst(440, 0.6, 'sawtooth', 0.8);
          break;
        case 'match.bell':
          [1320, 2640, 3960].forEach((f) => burst(f, 0.9, 'sine', f === 1320 ? 0.6 : 0.3));
          break;
        case 'match.victory': {
          const notes = [523, 659, 784, 1047, 1319];
          notes.forEach((f, i) => window.setTimeout(() => burst(f, 0.2, 'triangle', 0.9), i * 100));
          break;
        }
        case 'match.defeat': {
          const notes = [523, 440, 349, 262];
          notes.forEach((f, i) => window.setTimeout(() => burst(f, 0.3, 'sawtooth', 0.6), i * 140));
          break;
        }
        default:
          burst(660, 0.08);
      }
    } catch (e) {
      console.warn('[audio] synth failed', e);
    }
  }
}

export const audio = new AudioServiceImpl();
